import { mercuryApi } from '$api'
import { exceptionMessageHandler, logger } from '$utils'
import {
  QueryFunction,
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
  useQuery,
} from '@tanstack/react-query'

/**
 * Thin React Query wrapper for GET requests against the Mercury API. Keys the
 * query on the `url`, disables refetch-on-window-focus, and (by default)
 * surfaces fetch failures via `isError` so the caller can render its own error
 * state.
 *
 * @typeParam T - Shape of the response body.
 * @param url - Mercury API path to GET; also used as the React Query key.
 * @param options - Any `useQuery` options except `queryKey`/`queryFn` (which
 *   are managed here), e.g. `staleTime`, `enabled`, `retry`.
 * @param swallowError - Error-handling mode. Defaults to `false`.
 *   - `false` (default): the error is logged then re-thrown so the query
 *     *rejects* and surfaces via `isError`/`error`. No global snackbar is
 *     shown, so the caller is expected to render its own error UI. Note React
 *     Query's default retry/backoff still applies before `isError` flips — pass
 *     `retry: 0` via `options` to fail fast.
 *   - `true`: the error is logged, reported via the global snackbar
 *     (`exceptionMessageHandler`), and swallowed so the query *resolves* with
 *     `undefined`. `isError` stays `false`. Use this when a failed fetch should
 *     degrade gracefully (e.g. an empty section) rather than surface an error.
 * @returns The React Query `UseQueryResult<T>`.
 *
 * @example
 * // Surface failures (default) so the component can show its own error UI.
 * const { data, isLoading, isError } = useMercuryFetch<Subscription[]>(
 *   '/subscriptions',
 *   { staleTime: 5 * 60 * 1000 },
 * )
 *
 * @example
 * // Swallow failures — `data` is undefined on error, snackbar shown, no error state.
 * const { data, isLoading } = useMercuryFetch<Menu[]>('/menus', undefined, true)
 */
export function useMercuryFetch<T>(
  url: string,
  options?: Omit<UseQueryOptions<T>, 'queryKey' | 'queryFn'>,
  swallowError = false
) {
  const queryOptions: UseQueryOptions<T> = {
    ...options,
    refetchOnWindowFocus: false,
    queryKey: [url],
    queryFn: (async () =>
      mercuryApi.get(url).catch((error) => {
        logger.error(`Failed to get ${url}: ${error}`)
        if (!swallowError) {
          throw error
        }
        exceptionMessageHandler(error)
      })) as QueryFunction<T, QueryKey, never>,
  }

  const query: UseQueryResult<T> = useQuery<T>(queryOptions)

  return query
}
